import { AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useCurrentWorkspace } from '../../lib/workspace';
import { useIsSuperAdmin } from '../../lib/auth';
import { useChatWidgetStore } from './ChatWidgetStore';
import { ChatBubble } from './ChatBubble';
import { ChatWidgetPanel } from './ChatWidgetPanel';
import { ChatNotices } from './ChatNotices';
import { DominaBrief } from './DominaBrief';

/**
 * Floating chat widget — renders the bubble + expandable panel.
 * Hidden on the full-page /chat route to avoid overlap.
 * Only renders when a workspace is selected.
 */
export function ChatWidget() {
  const ws = useCurrentWorkspace();
  const { isOpen } = useChatWidgetStore();
  const location = useLocation();
  const isSuperAdmin = useIsSuperAdmin();

  // Don't show on the full-page chat route
  if (location.pathname === '/chat') return null;

  // Don't show if no workspace selected
  if (!ws) return null;

  return (
    <>
      {/* Mobile only: a solid bar docked directly above the tab bar that gives
          the two centred bubbles their OWN line — page content ends above it
          (reserved by <main>'s bottom padding), so content never sits behind the
          bubbles. Bottom = tab-bar height (3.5rem) + the iOS safe-area inset. */}
      {!isOpen && (
        <div
          aria-hidden
          className="fixed inset-x-0 bottom-[var(--mob-bottom,7rem)] z-30 h-16 border-t border-neutral-200 bg-white md:hidden"
        />
      )}
      {/* Tablet / laptop / desktop (md+): a slim fixed footer bar along the very
          bottom carrying the app footer menu (left-aligned). Starts after the
          64px icon rail; content is reserved above it by <main>'s bounded height.
          The chat bubbles sit in their OWN row just ABOVE this bar (see
          ChatBubble) rather than inside it, so the footer menu is never covered.
          Admin / Licenses links show only for super-admin accounts. */}
      <div className="fixed bottom-0 left-16 right-0 z-30 hidden h-14 items-center border-t border-neutral-200 bg-white px-5 md:flex">
        <div className="flex min-w-0 flex-1 items-center gap-x-3 overflow-hidden whitespace-nowrap pr-4 text-[11px] text-neutral-400">
          <span className="min-w-0 truncate">
            © {new Date().getFullYear()} iHeart Digital (Pty) Ltd · Reg. 2026/453657/07 · iHeart CNTRL is a subdivision of{' '}
            <a href="https://iheartdigital.co.za" target="_blank" rel="noopener noreferrer" className="font-medium text-pink-500 transition-colors hover:text-pink-600">iHeart Digital</a>
          </span>
          <a href="/terms-and-conditions/" className="transition-colors hover:text-neutral-600">Terms</a>
          <a href="/privacy-policy/" className="transition-colors hover:text-neutral-600">Privacy</a>
          <a href="/returns-and-refunds/" className="transition-colors hover:text-neutral-600">Refunds</a>
          {isSuperAdmin && <a href="/admin/tokens" className="transition-colors hover:text-neutral-600">Admin</a>}
          {isSuperAdmin && <a href="/admin/licenses" className="transition-colors hover:text-neutral-600">Licenses</a>}
        </div>
      </div>
      {/* Both bubbles render at every state. On lg they stay docked in the bar
          while a panel is open (each toggles its own panel); on mobile/tablet
          they hide behind the open panel. The brief + chat panels are mutually
          exclusive (opening one closes the other) so they never stack. */}
      <ChatBubble />
      <DominaBrief />
      {!isOpen && <ChatNotices />}
      <AnimatePresence>{isOpen && <ChatWidgetPanel />}</AnimatePresence>
    </>
  );
}
